// src/pages/VehicleV2/TypePositionListing.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const LIST_API = "https://nissi-india.com/api/typePositionListing";
const ENTRY_API = "https://nissi-india.com/api/typePosition";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const VehicleTypePositionListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const canAdd = useMemo(() => {
    const v = sessionStorage.getItem("typepos_addrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const canEdit = useMemo(() => {
    const v = sessionStorage.getItem("typepos_editrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const canDelete = useMemo(() => {
    const v = sessionStorage.getItem("typepos_deleterights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v =
      sessionStorage.getItem("BranchActive") ||
      localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [showForm, setShowForm] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  const [typePositionId, setTypePositionId] = useState(0);
  const [typePositionName, setTypePositionName] = useState("");
  const isEdit = typePositionId > 0;

  const totalPages = useMemo(
    () => (limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1),
    [total, limit]
  );

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const fetchList = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;
      const nextSearch = opts.search ?? search;

      try {
        setLoading(true);
        const { data } = await axios.get(`${LIST_API}/list`, {
          params: { search: nextSearch, page: nextPage, limit: nextLimit },
          headers: commonHeaders,
        });

        if (!data?.success) throw new Error(data?.message || "Failed to load list");

        const nextRows = Array.isArray(data.rows) ? data.rows : [];
        const nextTotal = toInt(data.total, 0);
        setRows(nextRows);
        setTotal(nextTotal);
        setSelectedIds([]);

        const computedTotalPages =
          nextLimit > 0 ? Math.max(1, Math.ceil(nextTotal / nextLimit)) : 1;
        if (nextPage > computedTotalPages) {
          setPage(computedTotalPages);
          return fetchList({
            page: computedTotalPages,
            limit: nextLimit,
            search: nextSearch,
          });
        }
        setPage(nextPage);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Server error");
        setRows([]);
        setTotal(0);
        setSelectedIds([]);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, search, commonHeaders]
  );

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const resetForm = () => {
    setTypePositionId(0);
    setTypePositionName("");
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEdit = async (row) => {
    const id = toInt(row?.fld_vehicletyre_positionid);
    if (!id) return;

    setShowForm(true);
    setTypePositionId(id);
    setTypePositionName(toStr(row?.fld_vehicletyre_positiontype));

    try {
      const { data } = await axios.get(`${ENTRY_API}/view`, {
        params: { typePositionId: id },
        headers: commonHeaders,
      });
      const rec = data?.data || {};
      setTypePositionName(
        toStr(
          rec.typePositionName ||
            rec.tyrePositionType ||
            rec.fld_vehicletyre_positiontype ||
            row?.fld_vehicletyre_positiontype
        )
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load record");
    }
  };

  const handleSave = async () => {
    const value = toStr(typePositionName);
    if (!value) return toast.error("Please enter Type Position");
    if (!branchActive) return toast.error("Branch is not active");
    if (isEdit && !canEdit) return toast.error("You do not have edit rights");
    if (!isEdit && !canAdd) return toast.error("You do not have add rights");

    const loginUserId =
      toInt(sessionStorage.getItem("loginUserId"), 0) ||
      toInt(localStorage.getItem("userId"), 0);

    try {
    setSaving(true);
    const { data } = await axios.post(
      `${ENTRY_API}/save`,
      {
        typePositionId: typePositionId || 0,
        typePositionName: value,
        loginUserId,
      },
      { headers: commonHeaders }
    );
    if (!data?.success) throw new Error(data?.message || "Failed to save");
    toast.success(data?.message || "Saved successfully");

    const stayOnPage = isEdit ? page : 1; // new row → first page
    resetForm();
    await fetchList({ page: stayOnPage });
  } catch (err) {
    toast.error(err?.response?.data?.message || err?.message || "Failed to save");
  } finally {
    setSaving(false);
  }
  };

  const handleDelete = async () => {
    if (!branchActive || !canDelete) return;
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one type position to delete");
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} selected record(s)?`)) return;

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${LIST_API}/delete`,
        { ids: selectedIds },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success(data?.message || "Deleted successfully");

      const remaining = Math.max(0, total - selectedIds.length);
      const remainingPages = limit > 0 ? Math.max(1, Math.ceil(remaining / limit)) : 1;
      const nextPage = Math.min(page, remainingPages);
      if (selectedIds.includes(typePositionId)) resetForm();
      await fetchList({ page: nextPage });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const allSelected =
    rows.length > 0 &&
    rows.every((x) => selectedIds.includes(toInt(x.fld_vehicletyre_positionid)));

  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else {
      setSelectedIds(
        rows.map((x) => toInt(x.fld_vehicletyre_positionid)).filter((x) => x > 0)
      );
    }
  };

  const toggleSelect = (id) => {
    const nid = toInt(id);
    setSelectedIds((prev) =>
      prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]
    );
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TYPE POSITION</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle Type Position
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              
              <button
                onClick={() => setShowForm((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {showForm && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  {isEdit ? "Edit Type Position" : "Add Type Position"}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={resetForm}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <FaTimes /> Clear
                    </span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={
                      saving ||
                      !toStr(typePositionName) ||
                      !branchActive ||
                      (isEdit ? !canEdit : !canAdd)
                    }
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                      saving || !toStr(typePositionName)
                        ? "opacity-60 cursor-not-allowed"
                        : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSave />
                    {saving ? "Saving..." : isEdit ? "Update" : "Save"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>
                    Type Position <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={typePositionName}
                    onChange={(e) => setTypePositionName(e.target.value)}
                    placeholder="Enter type position..."
                    className={inputClass}
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          )}

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSearch("");
                      fetchList({ page: 1, search: "" });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => fetchList({ page: 1, search })}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>Search Type Position</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchList({ page: 1, search })}
                  placeholder="Type to search..."
                  className={inputClass}
                />
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                Loading...
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                No Type Positions Found
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Type Position</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          disabled={!canDelete || rows.length === 0}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => {
                      const id = toInt(r.fld_vehicletyre_positionid);
                      return (
                        <tr
                          key={id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } ${
                            typePositionId === id
                              ? isDark
                                ? "ring-1 ring-blue-400"
                                : "bg-blue-50"
                              : ""
                          } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className="px-6 py-4 text-sm">
                            {r.slno ?? (page - 1) * limit + idx + 1}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {r.fld_vehicletyre_positiontype || "-"}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              disabled={!canEdit}
                              className="text-yellow-500 hover:text-yellow-600 text-lg"
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() => toggleSelect(id)}
                              disabled={!canDelete}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {canDelete && branchActive && (
                  <div className="p-4">
                    <button
                      onClick={handleDelete}
                      disabled={selectedIds.length === 0}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                        selectedIds.length === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      <FaTrash /> Remove
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> of <b>{totalPages}</b> | Total: <b>{total}</b>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={limit}
                onChange={(e) => {
                  const nl = toInt(e.target.value, 25);
                  setLimit(nl);
                  fetchList({ page: 1, limit: nl });
                }}
                className={inputClass.replace("w-full ", "w-auto ")}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <button
                onClick={() => fetchList({ page: page - 1 })}
                disabled={page <= 1}
                className={`px-3 py-2 border rounded-lg ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""} ${
                  isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Prev
              </button>
              <button
                onClick={() => fetchList({ page: page + 1 })}
                disabled={page >= totalPages}
                className={`px-3 py-2 border rounded-lg ${
                  page >= totalPages ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypePositionListing;